import type { LayerAWalkStrip } from "./case-manifest.js";

const DEFAULT_CORRIDOR_HALF = 7;

export function walkCorridorHalfWidth(strip: LayerAWalkStrip): number {
  return strip.corridor_half_width ?? DEFAULT_CORRIDOR_HALF;
}

/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
export function distSqPointToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq < 1e-12) {
    return apx * apx + apy * apy;
  }
  let t = (apx * abx + apy * aby) / abLenSq;
  t = Math.max(0, Math.min(1, t));
  const qx = ax + t * abx;
  const qy = ay + t * aby;
  const dx = px - qx;
  const dy = py - qy;
  return dx * dx + dy * dy;
}

const SPINE_SUBDIVS_PER_EDGE = 24;

/** Subdivided spine polyline for smooth distance fields and road center lines. */
export function densifySpinePolyline(strip: LayerAWalkStrip): { px: number[]; py: number[] } {
  const sx = strip.spine_tx;
  const sy = strip.spine_ty;
  const px: number[] = [];
  const py: number[] = [];
  if (!sx || !sy || sx.length < 2 || sx.length !== sy.length) {
    return { px, py };
  }
  for (let i = 0; i < sx.length - 1; i++) {
    const x0 = sx[i]! + 0.5;
    const y0 = sy[i]! + 0.5;
    const x1 = sx[i + 1]! + 0.5;
    const y1 = sy[i + 1]! + 0.5;
    for (let s = 0; s <= SPINE_SUBDIVS_PER_EDGE; s++) {
      const t = s / SPINE_SUBDIVS_PER_EDGE;
      px.push(x0 + (x1 - x0) * t);
      py.push(y0 + (y1 - y0) * t);
    }
  }
  return { px, py };
}

function minDistSqFromDenseSpine(
  dense: { px: number[]; py: number[] },
  px: number,
  py: number
): number {
  if (dense.px.length < 2) {
    return 0;
  }
  let best = Infinity;
  for (let i = 0; i < dense.px.length - 1; i++) {
    const d = distSqPointToSegment(
      px,
      py,
      dense.px[i]!,
      dense.py[i]!,
      dense.px[i + 1]!,
      dense.py[i + 1]!
    );
    if (d < best) best = d;
  }
  return best;
}

/** Cached distance from tile-space points to the densified spine (tile units). */
export function createSpineDistanceSampler(
  strip: LayerAWalkStrip
): (fx: number, fy: number) => number {
  const sx = strip.spine_tx;
  const sy = strip.spine_ty;
  if (!sx || !sy || sx.length < 2) {
    return () => 0;
  }
  const dense = densifySpinePolyline(strip);
  return (fx: number, fy: number) => Math.sqrt(minDistSqFromDenseSpine(dense, fx, fy));
}

const spineSamplerCache = new WeakMap<LayerAWalkStrip, (fx: number, fy: number) => number>();

function spineDistanceSampler(strip: LayerAWalkStrip): (fx: number, fy: number) => number {
  let sampler = spineSamplerCache.get(strip);
  if (!sampler) {
    sampler = createSpineDistanceSampler(strip);
    spineSamplerCache.set(strip, sampler);
  }
  return sampler;
}

function minDistSqToSpine(strip: LayerAWalkStrip, tx: number, ty: number): number {
  const d = spineDistanceSampler(strip)(tx + 0.5, ty + 0.5);
  return d * d;
}

/** Distance from arbitrary tile-space point to spine (tile units); corners use (tx, ty), (tx+1, ty), etc. */
export function tilePointDistToSpineTiles(
  strip: LayerAWalkStrip,
  fx: number,
  fy: number
): number {
  return spineDistanceSampler(strip)(fx, fy);
}

/** Distance from tile center to spine polyline (tile units). */
export function tileDistToSpineTiles(
  strip: LayerAWalkStrip,
  tx: number,
  ty: number
): number {
  const sx = strip.spine_tx;
  const sy = strip.spine_ty;
  if (!sx || !sy || sx.length < 2) {
    return 0;
  }
  return Math.sqrt(minDistSqToSpine(strip, tx, ty));
}

export function tileInWalkStrip(
  strip: LayerAWalkStrip,
  tx: number,
  ty: number
): boolean {
  const sx = strip.spine_tx;
  const sy = strip.spine_ty;
  if (!sx || !sy || sx.length < 2 || sx.length !== sy.length) {
    return (
      tx >= strip.tile_x0 &&
      tx <= strip.tile_x1 &&
      ty >= strip.tile_y0 &&
      ty <= strip.tile_y1
    );
  }
  const half = walkCorridorHalfWidth(strip);
  return minDistSqToSpine(strip, tx, ty) <= half * half + 1e-6;
}

export function walkStripToMask(
  strip: LayerAWalkStrip,
  gw: number,
  gh: number
): boolean[][] {
  const mask: boolean[][] = [];
  for (let y = 0; y < gh; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < gw; x++) {
      row.push(tileInWalkStrip(strip, x, y));
    }
    mask.push(row);
  }
  return mask;
}

/** Index of nearest spine vertex to a tile-space point. */
export function nearestSpineIndex(
  strip: LayerAWalkStrip,
  tx: number,
  ty: number
): number {
  const sx = strip.spine_tx;
  const sy = strip.spine_ty;
  if (!sx || !sy || sx.length < 1) return 0;
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < sx.length; i++) {
    const d = Math.abs(tx - sx[i]!) + Math.abs(ty - sy[i]!);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Unit vector from footprint center toward the walk strip spine (road-facing). */
export function footprintTowardRoad(
  strip: LayerAWalkStrip,
  tile_x: number,
  tile_y: number,
  footprintTiles: number
): { dx: number; dy: number } {
  const fp = footprintTiles;
  const fcx = tile_x + fp / 2;
  const fcy = tile_y + fp / 2;
  const snap = nearestSpineVertex(strip, fcx, fcy);
  const dx = snap.tx + 0.5 - fcx;
  const dy = snap.ty + 0.5 - fcy;
  const len = Math.hypot(dx, dy) || 1;
  return { dx: dx / len, dy: dy / len };
}

function blockWalkableTile(walkable: boolean[][], w: number, h: number, tx: number, ty: number): void {
  if (tx < 0 || ty < 0 || tx >= w || ty >= h) return;
  if (walkable[ty]?.[tx]) walkable[ty]![tx] = false;
}

function buildApproachKeys(
  strip: LayerAWalkStrip,
  p: { tile_x: number; tile_y: number },
  fp: number,
  w: number,
  h: number
): Set<string> {
  const cx = Math.floor(p.tile_x + fp / 2);
  const cy = Math.floor(p.tile_y + fp / 2);
  const snap = nearestSpineVertex(strip, cx, cy);
  const approachKeys = new Set<string>();
  for (const { tx, ty } of bresenhamTiles(snap.tx, snap.ty, cx, cy)) {
    approachKeys.add(`${tx},${ty}`);
    for (const [ox, oy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nx = tx + ox;
      const ny = ty + oy;
      if (nx >= 0 && ny >= 0 && nx < w && ny < h) approachKeys.add(`${nx},${ny}`);
    }
  }
  return approachKeys;
}

/** Snap to nearest spine vertex (good enough for NPC / spawn hints). */
export function nearestSpineVertex(
  strip: LayerAWalkStrip,
  tx: number,
  ty: number
): { tx: number; ty: number } {
  const sx = strip.spine_tx;
  const sy = strip.spine_ty;
  if (!sx || !sy || sx.length < 1 || sx.length !== sy.length) {
    return {
      tx: Math.max(strip.tile_x0, Math.min(strip.tile_x1, tx)),
      ty: Math.max(strip.tile_y0, Math.min(strip.tile_y1, ty)),
    };
  }
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < sx.length; i++) {
    const d = Math.abs(tx - sx[i]!) + Math.abs(ty - sy[i]!);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return { tx: sx[best]!, ty: sy[best]! };
}

/** Integer line from (x0,y0) to (x1,y1), inclusive endpoints. */
export function bresenhamTiles(
  x0: number,
  y0: number,
  x1: number,
  y1: number
): { tx: number; ty: number }[] {
  const out: { tx: number; ty: number }[] = [];
  let x = x0;
  let y = y0;
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sxv = x0 < x1 ? 1 : -1;
  const syv = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  for (;;) {
    out.push({ tx: x, ty: y });
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sxv;
    }
    if (e2 < dx) {
      err += dx;
      y += syv;
    }
  }
  return out;
}

/**
 * Marks sidewalk spurs from each building footprint toward the spine so players can reach doors.
 * Call after the corridor mask is built and building footprints are marked blocked.
 */
function stampWalkableTile(
  walkable: boolean[][],
  strip: LayerAWalkStrip,
  w: number,
  h: number,
  tx: number,
  ty: number
): void {
  if (tx < 0 || ty < 0 || tx >= w || ty >= h) return;
  if (tileInWalkStrip(strip, tx, ty)) walkable[ty]![tx] = true;
}

export function stampBuildingApproachPaths(
  walkable: boolean[][],
  strip: LayerAWalkStrip,
  placements: readonly { tile_x: number; tile_y: number }[],
  footprintTiles: number
): void {
  const h = walkable.length;
  const w = walkable[0]?.length ?? 0;
  const fp = footprintTiles;
  const neighbors = [
    [0, 0],
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const;
  for (const p of placements) {
    const cx = Math.floor(p.tile_x + fp / 2);
    const cy = Math.floor(p.tile_y + fp / 2);
    const snap = nearestSpineVertex(strip, cx, cy);
    for (const { tx, ty } of bresenhamTiles(snap.tx, snap.ty, cx, cy)) {
      for (const [dx, dy] of neighbors) {
        stampWalkableTile(walkable, strip, w, h, tx + dx, ty + dy);
      }
    }
    for (let dx = 0; dx < fp; dx++) {
      for (let dy = 0; dy < fp; dy++) {
        const tx = p.tile_x + dx;
        const ty = p.tile_y + dy;
        if (tileInWalkStrip(strip, tx, ty)) continue;
        for (const [ox, oy] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          stampWalkableTile(walkable, strip, w, h, tx + ox, ty + oy);
        }
      }
    }
  }
}

/** Tiles of non-walkable ring on back/sides of story footprints (road approach stays open). */
export function stampBuildingPerimeterFence(
  walkable: boolean[][],
  strip: LayerAWalkStrip,
  placements: readonly { tile_x: number; tile_y: number; decor?: boolean }[],
  footprintTiles: number,
  fenceRows = 2
): void {
  const h = walkable.length;
  const w = walkable[0]?.length ?? 0;
  const fp = footprintTiles;
  const roadFacingDot = 0.55;

  for (const p of placements) {
    if (p.decor === true) continue;
    const fcx = p.tile_x + fp / 2;
    const fcy = p.tile_y + fp / 2;
    const toward = footprintTowardRoad(strip, p.tile_x, p.tile_y, fp);
    const approachKeys = buildApproachKeys(strip, p, fp, w, h);

    for (let ring = 1; ring <= fenceRows; ring++) {
      for (let dy = -ring; dy < fp + ring; dy++) {
        for (let dx = -ring; dx < fp + ring; dx++) {
          const insideFoot =
            dx >= 0 && dy >= 0 && dx < fp && dy < fp;
          const onRing =
            !insideFoot &&
            (dx <= -1 || dy <= -1 || dx >= fp || dy >= fp) &&
            dx >= -ring &&
            dy >= -ring &&
            dx < fp + ring &&
            dy < fp + ring;
          if (!onRing) continue;
          const tx = p.tile_x + dx;
          const ty = p.tile_y + dy;
          if (tx < 0 || ty < 0 || tx >= w || ty >= h) continue;
          if (approachKeys.has(`${tx},${ty}`)) continue;
          const relX = tx + 0.5 - fcx;
          const relY = ty + 0.5 - fcy;
          const dot = relX * toward.dx + relY * toward.dy;
          if (dot > roadFacingDot) continue;
          blockWalkableTile(walkable, w, h, tx, ty);
        }
      }
    }
  }
}

/**
 * Continuous back-edge barrier between adjacent story buildings along the row
 * (parallel to the road) so players cannot slip behind façades.
 */
export function stampBuildingRowBackFence(
  walkable: boolean[][],
  strip: LayerAWalkStrip,
  placements: readonly { tile_x: number; tile_y: number; decor?: boolean }[],
  footprintTiles: number,
  connectorRows = 2
): void {
  const h = walkable.length;
  const w = walkable[0]?.length ?? 0;
  const fp = footprintTiles;
  const story = placements
    .filter((p) => p.decor !== true)
    .map((p) => ({
      p,
      si: nearestSpineIndex(strip, p.tile_x + fp / 2, p.tile_y + fp / 2),
    }))
    .sort((a, b) => a.si - b.si || a.p.tile_x - b.p.tile_x);

  for (let i = 0; i < story.length - 1; i++) {
    const a = story[i]!.p;
    const b = story[i + 1]!.p;
    const towardA = footprintTowardRoad(strip, a.tile_x, a.tile_y, fp);
    const tgX = -towardA.dy;
    const tgY = towardA.dx;
    const acx = a.tile_x + fp / 2;
    const acy = a.tile_y + fp / 2;
    const bcx = b.tile_x + fp / 2;
    const bcy = b.tile_y + fp / 2;
    const backDist = fp / 2 + 1.5;
    const ax = acx - towardA.dx * backDist + tgX * (fp / 2 + 0.5);
    const ay = acy - towardA.dy * backDist + tgY * (fp / 2 + 0.5);
    const bx = bcx - towardA.dx * backDist - tgX * (fp / 2 + 0.5);
    const by = bcy - towardA.dy * backDist - tgY * (fp / 2 + 0.5);
    const x0 = Math.round(ax);
    const y0 = Math.round(ay);
    const x1 = Math.round(bx);
    const y1 = Math.round(by);
    for (const { tx, ty } of bresenhamTiles(x0, y0, x1, y1)) {
      for (let dr = 0; dr < connectorRows; dr++) {
        blockWalkableTile(walkable, w, h, tx, ty + dr);
        blockWalkableTile(walkable, w, h, tx + 1, ty + dr);
        blockWalkableTile(walkable, w, h, tx, ty - dr);
        blockWalkableTile(walkable, w, h, tx - 1, ty - dr);
      }
    }
  }
}
